/**
 * Rarity and Series Color Management for Fortnite Shop items.
 * Supports canonical Fortnite rarities, official series, and custom materialInstance colors.
 */

export interface RarityConfig {
  name: string;
  gradient: string;
  borderClass: string;
  tagColor: string;
}

export type SupportedRarityKey =
  | 'gaming'
  | 'marvel'
  | 'dc'
  | 'starwars'
  | 'icon'
  | 'shadow'
  | 'frozen'
  | 'lava'
  | 'dark'
  | 'mythic'
  | 'exotic'
  | 'legendary'
  | 'epic'
  | 'rare'
  | 'uncommon'
  | 'common';

export const RARITY_CONFIGS: Record<string, RarityConfig> = {
  gaming: {
    name: 'Serie de Videojuegos',
    gradient: 'linear-gradient(180deg, #ffc700 0%, #ff8c00 100%)',
    borderClass: 'border-yellow-400',
    tagColor: '#ffc700',
  },
  marvel: {
    name: 'Marvel',
    gradient: 'linear-gradient(180deg, #ed1d24 0%, #a80e14 100%)',
    borderClass: 'border-red-500',
    tagColor: '#ed1d24',
  },
  dc: {
    name: 'DC',
    gradient: 'linear-gradient(180deg, #0055ff 0%, #0033aa 100%)',
    borderClass: 'border-blue-600',
    tagColor: '#0055ff',
  },
  starwars: {
    name: 'Star Wars',
    gradient: 'linear-gradient(180deg, #3d5a80 0%, #1d2d44 100%)',
    borderClass: 'border-slate-500',
    tagColor: '#3d5a80',
  },
  icon: {
    name: 'Serie de Ídolos',
    gradient: 'linear-gradient(180deg, #2be4cb 0%, #159f8e 100%)',
    borderClass: 'border-cyan-400',
    tagColor: '#2be4cb',
  },
  shadow: {
    name: 'Serie de Sombras',
    gradient: 'linear-gradient(180deg, #475569 0%, #0f172a 100%)',
    borderClass: 'border-slate-600',
    tagColor: '#64748b',
  },
  frozen: {
    name: 'Serie Congelada',
    gradient: 'linear-gradient(180deg, #7dd3fc 0%, #0284c7 100%)',
    borderClass: 'border-sky-300',
    tagColor: '#7dd3fc',
  },
  lava: {
    name: 'Serie de Lava',
    gradient: 'linear-gradient(180deg, #f97316 0%, #991b1b 100%)',
    borderClass: 'border-orange-600',
    tagColor: '#f97316',
  },
  dark: {
    name: 'Serie Oscura',
    gradient: 'linear-gradient(180deg, #c026d3 0%, #581c87 100%)',
    borderClass: 'border-fuchsia-600',
    tagColor: '#c026d3',
  },
  mythic: {
    name: 'Mítico',
    gradient: 'linear-gradient(180deg, #facc15 0%, #ca8a04 100%)',
    borderClass: 'border-yellow-400',
    tagColor: '#facc15',
  },
  exotic: {
    name: 'Exótico',
    gradient: 'linear-gradient(180deg, #2dd4bf 0%, #0f766e 100%)',
    borderClass: 'border-teal-400',
    tagColor: '#2dd4bf',
  },
  legendary: {
    name: 'Legendario',
    gradient: 'linear-gradient(180deg, #f59e0b 0%, #d97706 100%)',
    borderClass: 'border-amber-500',
    tagColor: '#f59e0b',
  },
  epic: {
    name: 'Épico',
    gradient: 'linear-gradient(180deg, #c026d3 0%, #9333ea 100%)',
    borderClass: 'border-purple-500',
    tagColor: '#c026d3',
  },
  rare: {
    name: 'Raro',
    gradient: 'linear-gradient(180deg, #0ea5e9 0%, #0284c7 100%)',
    borderClass: 'border-sky-500',
    tagColor: '#0ea5e9',
  },
  uncommon: {
    name: 'Poco Común',
    gradient: 'linear-gradient(180deg, #22c55e 0%, #16a34a 100%)',
    borderClass: 'border-emerald-500',
    tagColor: '#22c55e',
  },
  common: {
    name: 'Común',
    gradient: 'linear-gradient(180deg, #4b5563 0%, #374151 100%)',
    borderClass: 'border-gray-500',
    tagColor: '#4b5563',
  },
};

/**
 * Normalizes string input safely by converting to lowercase and stripping diacritics.
 * Defensively handles null, undefined, numbers, and malformed inputs.
 */
function normalizeText(text: unknown): string {
  if (text == null) return '';
  const str = typeof text === 'string' ? text : String(text);
  return str
    .toLowerCase()
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '')
    .trim();
}

export interface RarityItemInput {
  series?: string | null;
  rarity?: string | null;
  colors?: { a?: string; b?: string } | null;
}

/**
 * Identifies the canonical rarity or series key for an item.
 * Evaluates series identifiers BEFORE standard rarities to ensure
 * series like Gaming Legends ("gaminglegends") are never misclassified as Legendary.
 * Inspects both `item.series` and `item.rarity` to support FastAPI backend payloads.
 */
export function getRarityKey(item?: RarityItemInput | null): SupportedRarityKey {
  if (!item || typeof item !== 'object') {
    return 'common';
  }

  const seriesNorm = normalizeText(item.series);
  const rarityNorm = normalizeText(item.rarity);
  const combined = `${seriesNorm} ${rarityNorm}`.trim();

  if (!combined) {
    return 'common';
  }

  // 1. Series Checks (Priority 1: Must precede standard rarities)
  // Gaming Legends (checked before "legend" to prevent "gaminglegends" -> legendary)
  if (combined.includes('gaming') || combined.includes('videojuego')) {
    return 'gaming';
  }

  // Marvel
  if (combined.includes('marvel')) {
    return 'marvel';
  }

  // DC
  if (combined.includes('dc')) {
    return 'dc';
  }

  // Star Wars
  if (combined.includes('star')) {
    return 'starwars';
  }

  // Icon Series / Serie de Ídolos
  if (combined.includes('icon') || combined.includes('idolo')) {
    return 'icon';
  }

  // Shadow Series / Serie de Sombras
  if (combined.includes('shadow') || combined.includes('sombra')) {
    return 'shadow';
  }

  // Frozen Series / Serie Congelada
  if (combined.includes('frozen') || combined.includes('congelad')) {
    return 'frozen';
  }

  // Lava Series / Serie de Lava
  if (combined.includes('lava')) {
    return 'lava';
  }

  // Dark Series / Serie Oscura
  if (combined.includes('dark') || combined.includes('oscur')) {
    return 'dark';
  }

  // 2. Special Tier Rarities (Mythic / Exotic)
  if (combined.includes('mythic') || combined.includes('mitic')) {
    return 'mythic';
  }

  if (combined.includes('exotic')) {
    return 'exotic';
  }

  // 3. Standard Fortnite Rarities
  if (combined.includes('legend') || combined.includes('leyenda')) {
    return 'legendary';
  }

  if (combined.includes('epic')) {
    return 'epic';
  }

  if (combined.includes('rare') || combined.includes('raro')) {
    return 'rare';
  }

  if (combined.includes('uncommon') || combined.includes('poco')) {
    return 'uncommon';
  }

  if (combined.includes('common') || combined.includes('comun')) {
    return 'common';
  }

  return 'common';
}

/**
 * Returns full configuration object for an item's rarity or series.
 */
export function getRarityConfig(item?: RarityItemInput | null): RarityConfig {
  const key = getRarityKey(item);
  return RARITY_CONFIGS[key] || RARITY_CONFIGS.common;
}

/**
 * Resolves the background gradient style for an item entry.
 * Inspects series, rarity, and materialInstances colors.
 * Safe against null, undefined, empty, or malformed inputs.
 */
export function getItemGradient(item?: RarityItemInput | null): string {
  if (!item || typeof item !== 'object') {
    return RARITY_CONFIGS.common.gradient;
  }

  const key = getRarityKey(item);
  if (key !== 'common') {
    return RARITY_CONFIGS[key]?.gradient || RARITY_CONFIGS.common.gradient;
  }

  // Fallback for custom materialInstances colors if item is not a recognized series/rarity
  if (
    item.colors &&
    typeof item.colors === 'object' &&
    typeof item.colors.a === 'string' &&
    typeof item.colors.b === 'string' &&
    item.colors.a.trim() &&
    item.colors.b.trim()
  ) {
    const rawA = item.colors.a.trim();
    const rawB = item.colors.b.trim();
    const colA = rawA.startsWith('#') ? rawA : `#${rawA}`;
    const colB = rawB.startsWith('#') ? rawB : `#${rawB}`;
    return `linear-gradient(180deg, ${colA} 0%, ${colB} 100%)`;
  }

  return RARITY_CONFIGS.common.gradient;
}

/**
 * Returns Tailwind border class for an item.
 */
export function getItemBorderClass(item?: RarityItemInput | null): string {
  return getRarityConfig(item).borderClass;
}

/**
 * Returns hex color code for tag badges.
 */
export function getItemTagColor(item?: RarityItemInput | null): string {
  return getRarityConfig(item).tagColor;
}

